import type { Color } from "@salestrak/ui";
import type { WidgetSeed } from "#app/widgets/dashboards/use-widget-view";
import type { Omittable } from "@salestrak/ui";

/**
 * The managed-surface contract, shared by every managed catalog (My Work, Team
 * Overview, …). It lives in this neutral module rather than any one surface's
 * catalog so surfaces can depend on the shape without importing each other.
 *
 * A managed widget is a `WidgetSeed` plus the metadata the Manage-widgets panel
 * and the host effects read: a stable `widgetKey` (matched back to a placed
 * instance via the key `managedSeed` bakes into `config`), display copy, and
 * whether it seeds on a first empty visit.
 */
export type ManagedWidget = WidgetSeed & Omittable<{
  widgetKey: string;
  label: string;
  description: string;
  color?: Color;
  defaultOn: boolean;
}>;

/**
 * Bake a surface's managed-layout revision into a widget config. Each surface
 * defines ONE version const and threads it through this helper — never a
 * per-widget literal, since one missed literal on a bump makes that widget
 * re-migrate on every visit (D11).
 */
export function withManagedLayoutVersion(
  version: number,
  config: Record<string, unknown> = {},
): Record<string, unknown> {
  return { ...config, managedLayoutVersion: version };
}

/** Flatten a managed catalog entry into the `WidgetSeed` the store persists,
 *  re-baking `widgetKey` into config so the instance can be matched later. */
export function managedSeed(widget: ManagedWidget): WidgetSeed {
  return {
    type: widget.type,
    config: { ...widget.config, widgetKey: widget.widgetKey },
    x: widget.x,
    y: widget.y,
    w: widget.w,
    h: widget.h,
  };
}
